/**
 * Page container — the one top-level wrapper for every routed page. Applies
 * the page background and the responsive page padding.
 *
 *   <PageContainer>
 *     <PageHeading title="Manage Camps" />
 *     <Card>…</Card>
 *   </PageContainer>
 *
 * Pages are full width (Dan 2026-10-31). The 2026-10-31 audit of all 80
 * staff routes found eight different content widths (1280, 1152, 1024, 896,
 * 768, 672px and uncapped), every capped one centered — so on a wide screen
 * a page lost up to ~490px of gutter per side and its heading sat at a
 * different x from the page before it.
 *
 * `width="form"` caps a single-column form at a readable width. It stays
 * left-aligned so the heading does not move.
 *
 * Do not put another `max-w-* mx-auto` wrapper inside this. That re-creates
 * the gutter this component exists to remove.
 */

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getPageBackground } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** "full" (default) fills the content area; "form" caps it, left-aligned. */
  width?: "full" | "form";
  children?: React.ReactNode;
}

const PageContainer: React.FC<PageContainerProps> = ({
  width = "full",
  className = "",
  children,
  ...rest
}) => {
  const { isDarkMode } = useTheme();

  return (
    <div
      className={`min-h-screen ${getPageBackground(isDarkMode)} ${TOKENS.page.container} ${className}`}
      {...rest}
    >
      {width === "form" ? <div className={TOKENS.page.formWidth}>{children}</div> : children}
    </div>
  );
};

export default PageContainer;
